import { useEffect, useState } from "react";
import { api, AMENITY_LABELS, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Check, X, Clock, ChatCircle } from "@phosphor-icons/react";
import PhotoUpload, { toDisplayUrl } from "@/components/PhotoUpload";
import ScheduleEditor from "@/components/ScheduleEditor";
import MessagesDialog from "@/components/MessagesDialog";

const EMPTY = { title: "", description: "", hourly_rate: 12, neighborhood: "", address: "", photos: [], amenities: [], insurance_policy_number: "", insurance_expires: "" };

export default function HostDashboard() {
  const [tab, setTab] = useState("requests");
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [agreedHostWaiver, setAgreedHostWaiver] = useState(false);
  const [msgBooking, setMsgBooking] = useState(null);
  const [unread, setUnread] = useState({});

  const refresh = async () => {
    const [l, b, u] = await Promise.all([api.get("/host/listings"), api.get("/host/bookings"), api.get("/messages/unread")]);
    setListings(l.data); setBookings(b.data); setUnread(u.data || {});
  };

  useEffect(() => {
    refresh();
    api.get("/listings/amenities").then((r) => setAmenities(r.data));
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setAgreedHostWaiver(false); setDialogOpen(true); };
  const openEdit = (l) => {
    setEditing(l);
    setForm({ ...l, photos: l.photos || [] });
    setAgreedHostWaiver(true); // no re-consent needed on edits
    setDialogOpen(true);
  };

  const save = async () => {
    if (!editing && !agreedHostWaiver) {
      toast.error("Please accept the host listing waiver to publish");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title,
      description: form.description,
      hourly_rate: Number(form.hourly_rate),
      neighborhood: form.neighborhood,
      address: form.address,
      photos: form.photos,
      amenities: form.amenities,
      insurance_policy_number: form.insurance_policy_number || null,
      insurance_expires: form.insurance_expires || null,
      agreed_waiver: true,
    };
    try {
      if (editing) await api.put(`/listings/${editing.id}`, payload);
      else await api.post("/listings", payload);
      toast.success(editing ? "Listing updated" : "Listing published");
      setDialogOpen(false);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!confirm("Delete this listing?")) return;
    try { await api.delete(`/listings/${id}`); toast.success("Listing removed"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status });
      toast.success(`Booking ${status}`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const requestsPending = bookings.filter((b) => b.status === "requested");
  const requestsOther = bookings.filter((b) => b.status !== "requested");

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex items-end justify-between border-b border-neutral-200 pb-8 mb-8">
        <div>
          <div className="label-eyebrow mb-2">Host</div>
          <h1 className="font-display font-medium text-4xl tracking-tight">Your dashboard</h1>
        </div>
        <Button data-testid="new-listing-btn" onClick={openCreate} className="h-11 rounded-md bg-neutral-900 hover:bg-neutral-800 text-white">
          <Plus size={16} className="mr-1"/> New listing
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto bg-transparent border-b border-neutral-200 rounded-none w-full justify-start p-0 gap-8">
          <TabsTrigger data-testid="tab-requests" value="requests" className="rounded-none border-b-2 border-transparent data-[state=active]:border-neutral-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 py-3">
            Requests <span className="ml-2 text-xs bg-neutral-900 text-white px-1.5 rounded">{requestsPending.length}</span>
          </TabsTrigger>
          <TabsTrigger data-testid="tab-listings" value="listings" className="rounded-none border-b-2 border-transparent data-[state=active]:border-neutral-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 py-3">
            Listings <span className="ml-2 text-xs text-neutral-500">{listings.length}</span>
          </TabsTrigger>
          <TabsTrigger data-testid="tab-history" value="history" className="rounded-none border-b-2 border-transparent data-[state=active]:border-neutral-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 py-3">
            History
          </TabsTrigger>
          <TabsTrigger data-testid="tab-schedule" value="schedule" className="rounded-none border-b-2 border-transparent data-[state=active]:border-neutral-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 py-3">
            <Clock size={14} className="mr-1"/> Availability
          </TabsTrigger>
        </TabsList>

        {/* Requests */}
        <TabsContent value="requests" className="mt-8">
          {requestsPending.length === 0 ? (
            <EmptyBox text="No pending requests."/>
          ) : (
            <div className="border border-neutral-200 divide-y divide-neutral-200">
              {requestsPending.map((b) => (
                <div key={b.id} data-testid={`request-${b.id}`} className="p-5 flex flex-col md:flex-row md:items-center gap-4">
                  <img src={toDisplayUrl(b.listing?.photos?.[0])} alt="" className="w-24 h-16 object-cover border border-neutral-200"/>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{b.listing?.title}</div>
                    <div className="text-sm text-neutral-500">
                      {b.guest?.name} · {b.date} · {String(b.start_hour).padStart(2,"0")}:00–{String(b.end_hour).padStart(2,"0")}:00
                    </div>
                  </div>
                  <div className="font-display font-semibold text-lg">${b.total_price}</div>
                  <div className="flex gap-2">
                    <Button data-testid={`msg-req-${b.id}`} variant="outline" className="h-9 border-neutral-300 relative" onClick={() => setMsgBooking(b)}>
                      <ChatCircle size={14} className="mr-1"/> Message
                      {unread[b.id] > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] bg-red-600 text-white rounded-full flex items-center justify-center">{unread[b.id]}</span>}
                    </Button>
                    <Button data-testid={`decline-${b.id}`} variant="outline" className="h-9 border-neutral-300" onClick={() => setStatus(b.id, "declined")}>
                      <X size={14} className="mr-1"/> Decline
                    </Button>
                    <Button data-testid={`approve-${b.id}`} className="h-9 bg-neutral-900 hover:bg-neutral-800" onClick={() => setStatus(b.id, "confirmed")}>
                      <Check size={14} className="mr-1"/> Approve
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Listings */}
        <TabsContent value="listings" className="mt-8">
          {listings.length === 0 ? (
            <EmptyBox text="No listings yet. Create one to get discovered."/>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {listings.map((l) => (
                <div key={l.id} data-testid={`host-listing-${l.id}`} className="border border-neutral-200 overflow-hidden">
                  <div className="aspect-[16/9] overflow-hidden bg-neutral-100">
                    <img src={toDisplayUrl(l.photos?.[0])} alt="" className="w-full h-full object-cover"/>
                  </div>
                  <div className="p-5">
                    <div className="flex justify-between items-start gap-3">
                      <div>
                        <div className="font-display font-medium">{l.title}</div>
                        <div className="text-xs text-neutral-500 mt-1">{l.neighborhood}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">${l.hourly_rate}</div>
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">/ hour</div>
                      </div>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <Button data-testid={`edit-${l.id}`} variant="outline" size="sm" className="border-neutral-300" onClick={() => openEdit(l)}>
                        <PencilSimple size={14} className="mr-1"/> Edit
                      </Button>
                      <Button data-testid={`delete-${l.id}`} variant="outline" size="sm" className="border-neutral-300 text-red-600" onClick={() => del(l.id)}>
                        <Trash size={14} className="mr-1"/> Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* History */}
        <TabsContent value="history" className="mt-8">
          {requestsOther.length === 0 ? (
            <EmptyBox text="No completed or declined bookings yet."/>
          ) : (
            <div className="border border-neutral-200 divide-y divide-neutral-200">
              {requestsOther.map((b) => (
                <div key={b.id} className="p-5 flex items-center gap-4">
                  <div className="flex-1">
                    <div className="font-medium">{b.listing?.title}</div>
                    <div className="text-sm text-neutral-500">
                      {b.guest?.name} · {b.date} · {String(b.start_hour).padStart(2,"0")}:00–{String(b.end_hour).padStart(2,"0")}:00
                    </div>
                  </div>
                  <StatusPill s={b.status}/>
                  <div className="font-medium">${b.total_price}</div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Schedule */}
        <TabsContent value="schedule" className="mt-8">
          {listings.length === 0 ? (
            <EmptyBox text="Create a listing first, then set its weekly hours here."/>
          ) : (
            <div className="space-y-6">
              {listings.map((l) => <ScheduleEditor key={l.id} listing={l}/>)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <MessagesDialog open={!!msgBooking} onClose={() => { setMsgBooking(null); refresh(); }} booking={msgBooking}/>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display font-medium text-2xl">
              {editing ? "Edit listing" : "New listing"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Title">
              <Input data-testid="form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-11 border-neutral-300"/>
            </Field>
            <Field label="Description">
              <Textarea data-testid="form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border-neutral-300 min-h-24"/>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Hourly rate ($)">
                <Input data-testid="form-rate" type="number" min="1" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} className="h-11 border-neutral-300"/>
              </Field>
              <Field label="Neighborhood">
                <Input data-testid="form-neighborhood" value={form.neighborhood} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} className="h-11 border-neutral-300"/>
              </Field>
            </div>
            <Field label="Address">
              <Input data-testid="form-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-11 border-neutral-300"/>
              <div className="text-xs text-neutral-500 mt-1">We'll drop a map pin for you automatically.</div>
            </Field>
            <Field label="Photos">
              <PhotoUpload photos={form.photos} onChange={(next) => setForm({ ...form, photos: next })} testId="form-photo-upload"/>
            </Field>
            <Field label="Amenities">
              <div className="grid grid-cols-2 gap-2">
                {amenities.map((a) => (
                  <label key={a} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      data-testid={`form-amenity-${a}`}
                      checked={form.amenities.includes(a)}
                      onCheckedChange={() =>
                        setForm({
                          ...form,
                          amenities: form.amenities.includes(a)
                            ? form.amenities.filter((x) => x !== a)
                            : [...form.amenities, a],
                        })
                      }
                      className="border-neutral-400 data-[state=checked]:bg-neutral-900 data-[state=checked]:border-neutral-900"
                    />
                    {AMENITY_LABELS[a] || a}
                  </label>
                ))}
              </div>
            </Field>
            <div className="border border-neutral-200 p-4 space-y-3 bg-neutral-50">
              <div className="label-eyebrow">Insurance (recommended — see Terms §6)</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-neutral-500 mb-1">Policy number</div>
                  <Input data-testid="form-insurance-number" value={form.insurance_policy_number || ""} onChange={(e) => setForm({ ...form, insurance_policy_number: e.target.value })} placeholder="e.g. HP-83412-99" className="h-10 border-neutral-300 bg-white"/>
                </div>
                <div>
                  <div className="text-xs text-neutral-500 mb-1">Expires on</div>
                  <Input data-testid="form-insurance-expires" type="date" value={form.insurance_expires || ""} onChange={(e) => setForm({ ...form, insurance_expires: e.target.value })} className="h-10 border-neutral-300 bg-white"/>
                </div>
              </div>
              <div className="text-[11px] text-neutral-500">
                Guests only see "Insurance on file — expires MM/YYYY". Your policy number is never shown to guests.
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              {!editing && (
                <div className="flex-1 mr-3">
                  <label className="flex items-start gap-2 text-[12px] leading-relaxed cursor-pointer select-none border border-neutral-200 p-3">
                    <Checkbox
                      data-testid="host-waiver-checkbox"
                      checked={agreedHostWaiver}
                      onCheckedChange={(v) => setAgreedHostWaiver(!!v)}
                      className="mt-0.5 border-neutral-400 data-[state=checked]:bg-neutral-900 data-[state=checked]:border-neutral-900"
                    />
                    <span className="text-neutral-700">
                      I confirm I have the right to offer this space for rental use, that I carry or will
                      obtain insurance appropriate for hosting paying guests in my home, and that I release
                      DenShare from liability for any injury, loss, theft, or damage arising from a Guest's
                      visit, except where caused by DenShare's own gross negligence.
                    </span>
                  </label>
                </div>
              )}
              <div className="flex flex-col gap-2 justify-end">
                <div className="flex gap-2">
                  <Button variant="outline" className="border-neutral-300" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button
                    data-testid="form-save"
                    onClick={save}
                    disabled={saving || (!editing && !agreedHostWaiver)}
                    className="bg-neutral-900 hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {saving ? "Saving..." : editing ? "Save changes" : "Publish listing"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="label-eyebrow mb-1.5">{label}</div>
      {children}
    </div>
  );
}
function EmptyBox({ text }) {
  return <div className="border border-dashed border-neutral-300 p-16 text-center text-neutral-500 text-sm">{text}</div>;
}
function StatusPill({ s }) {
  const colors = {
    confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    declined: "bg-red-50 text-red-700 border-red-200",
    completed: "bg-neutral-100 text-neutral-700 border-neutral-200",
    requested: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return <span className={`text-[11px] uppercase tracking-widest px-2 py-1 border ${colors[s] || ""}`}>{s}</span>;
}
